import React from 'react';

/**
 * Table of deposits with inline editing support.
 * Props:
 *   deposits           - array of deposit objects
 *   editingDeposit     - currently edited deposit or null
 *   setEditingDeposit  - setter for editingDeposit
 *   onEdit(deposit)    - start editing a row
 *   onSaveEdit()       - save the edited row
 *   onDelete(deposit, forceDelete) - delete or deactivate
 *   onManageProducts(deposit) - open product assignment modal
 *   onAssignDepositProduct(deposit) - open deposit-product assignment modal
 */
const DepositTable = ({
  deposits,
  editingDeposit, setEditingDeposit,
  onEdit, onSaveEdit, onDelete,
  onManageProducts, onAssignDepositProduct
}) => (
  <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ backgroundColor: 'var(--bg)' }}>
          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nazwa kaucji</th>
          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Produkt w systemie</th>
          <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota</th>
          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Produkty objete</th>
          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Akcje</th>
        </tr>
      </thead>
      <tbody>
        {deposits.length === 0 ? (
          <tr>
            <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              Brak zdefiniowanych kaucji. Kliknij "Dodaj kaucje" aby utworzyc pierwsza.
            </td>
          </tr>
        ) : (
          deposits.map(deposit => (
            <DepositRow
              key={deposit.id}
              deposit={deposit}
              editingDeposit={editingDeposit}
              setEditingDeposit={setEditingDeposit}
              onEdit={onEdit}
              onSaveEdit={onSaveEdit}
              onDelete={onDelete}
              onManageProducts={onManageProducts}
              onAssignDepositProduct={onAssignDepositProduct}
            />
          ))
        )}
      </tbody>
    </table>
  </div>
);

const DepositRow = ({
  deposit, editingDeposit, setEditingDeposit,
  onEdit, onSaveEdit, onDelete, onManageProducts, onAssignDepositProduct
}) => {
  const isEditing = editingDeposit && editingDeposit.id === deposit.id;

  if (isEditing) {
    return (
      <tr style={{ borderBottom: '1px solid var(--border)', opacity: deposit.aktywny ? 1 : 0.5 }}>
        <td style={{ padding: '8px' }}>
          <input
            type="text"
            value={editingDeposit.nazwa}
            onChange={(e) => setEditingDeposit({ ...editingDeposit, nazwa: e.target.value })}
            style={{ width: '100%', padding: '6px', border: '1px solid var(--border)', borderRadius: '4px' }}
          />
        </td>
        <td style={{ padding: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>{deposit.produkt_nazwa || '-'}</td>
        <td style={{ padding: '8px' }}>
          <input
            type="number"
            step="0.01"
            value={editingDeposit.kwota}
            onChange={(e) => setEditingDeposit({ ...editingDeposit, kwota: e.target.value })}
            style={{ width: '80px', padding: '6px', border: '1px solid var(--border)', borderRadius: '4px', textAlign: 'right' }}
          />
        </td>
        <td style={{ padding: '8px', textAlign: 'center' }}>{deposit.produkty_count || 0}</td>
        <td style={{ padding: '8px', textAlign: 'center' }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={editingDeposit.aktywny}
              onChange={(e) => setEditingDeposit({ ...editingDeposit, aktywny: e.target.checked })}
            />
            {' '}Aktywna
          </label>
        </td>
        <td style={{ padding: '8px', textAlign: 'center' }}>
          <button
            onClick={onSaveEdit}
            style={{ padding: '4px 8px', border: '1px solid var(--success)', color: 'var(--success)' , borderRadius: '4px', cursor: 'pointer', marginRight: '5px' }}
          >
            Zapisz
          </button>
          <button
            onClick={() => setEditingDeposit(null)}
            style={{ padding: '4px 8px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , borderRadius: '4px', cursor: 'pointer' }}
          >
            Anuluj
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid var(--border)', opacity: deposit.aktywny ? 1 : 0.5 }}>
      <td style={{ padding: '12px' }}>
        <strong>{deposit.nazwa}</strong>
        {deposit.opis && <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{deposit.opis}</div>}
      </td>
      <td style={{ padding: '12px', color: 'var(--info)', fontSize: '12px' }}>
        {deposit.produkt_nazwa ? (
          <>
            {deposit.produkt_nazwa}
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>ID: {deposit.produkt_id}</div>
          </>
        ) : (
          <div>
            <span style={{ color: 'var(--danger)', fontWeight: '600' }}>Brak produktu!</span>
            <button
              onClick={() => onAssignDepositProduct(deposit)}
              style={{ display: 'block', marginTop: '4px', padding: '2px 8px', backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
            >
              Przypisz produkt
            </button>
          </div>
        )}
      </td>
      <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
        {parseFloat(deposit.kwota).toFixed(2)} zl
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        <button
          onClick={() => onManageProducts(deposit)}
          style={{ padding: '4px 10px', border: '1px solid var(--info)', color: 'var(--info)' , borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
        >
          {deposit.produkty_count || 0} produktow
        </button>
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        <span style={{
          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
          backgroundColor: deposit.aktywny ? 'var(--success-light)' : 'var(--danger-light)',
          color: deposit.aktywny ? 'var(--success)' : 'var(--danger)'
        }}>
          {deposit.aktywny ? 'Aktywna' : 'Nieaktywna'}
        </span>
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        <button
          onClick={() => onEdit(deposit)}
          style={{ padding: '4px 8px', border: '1px solid var(--info)', color: 'var(--info)' , borderRadius: '4px', cursor: 'pointer', marginRight: '5px', fontSize: '12px' }}
        >
          Edytuj
        </button>
        <button
          onClick={() => onDelete(deposit, false)}
          style={{ padding: '4px 8px', backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginRight: '5px' }}
          title="Dezaktywuj kaucje (mozna pozniej przywrocic)"
        >
          Dezaktywuj
        </button>
        <button
          onClick={() => onDelete(deposit, true)}
          style={{ padding: '4px 8px', border: '1px solid var(--danger)', color: 'var(--danger)' , borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          title="Trwale usun kaucje (nieodwracalne!)"
        >
          Usun
        </button>
      </td>
    </tr>
  );
};

export default DepositTable;
